import { useMemo } from "react";
import { useSelector } from "react-redux";

import { capitalizeEachWord } from "@/helpers/string";
import { RootState } from "@/store";
import { AggregationType, OrderByType, WidgetType } from "@/types/widget";
import { removeSpecialCharacters } from "@/utils/string";

import { DIMENSIONAL_VALUES_SHOWN } from "./constant";

interface UseWidgetConfigurationProps {
  datasetId: string;
  widgetType: WidgetType;
  measure: string;
  /** Currently selected dimension. When it is a breakdown group, measureOptions
   *  narrows to that same group — the group's columns are the dimension's values,
   *  so no other measure can be paired with it. */
  dimension?: string;
  /** When present, dimension and measure options are sourced from rawDatasets columns
   *  instead of the schema — the analysis result may contain enriched attributes
   *  not present in the pre-analysis schema. */
  analysisId?: string;
}

const toOption = (value: string, label: string) => ({ value, label });

const ALL_AGGREGATION_OPTIONS = Object.values(AggregationType).map((agg) =>
  toOption(agg, removeSpecialCharacters(agg))
);

const orderByOptions = Object.values(OrderByType).map((orderBy) =>
  toOption(orderBy, orderBy)
);

const dimensionValueShownOptions = DIMENSIONAL_VALUES_SHOWN.map((num) =>
  toOption(num, num)
);

const useWidgetConfiguration = ({
  datasetId,
  widgetType,
  measure,
  dimension,
  analysisId
}: UseWidgetConfigurationProps) => {
  const { datasets, rawDatasets } = useSelector(
    (state: RootState) => state.dataset
  );

  const datasetOptions = useMemo(
    () =>
      Object.values(datasets)
        .reverse()
        .map((dataset) => {
          if (dataset.data.published) {
            return toOption(
              dataset.data.id,
              dataset.data.display_name || dataset.data.name
            );
          }
          return false;
        })
        .filter(
          (opt): opt is { value: string; label: string } => opt !== false
        ),
    [datasets]
  );

  const isH3Dataset =
    datasets[datasetId]?.data.spatial_aggregation_type === "H3";

  // WIDE_DATA datasets group multiple columns under a shared breakdown_group_id.
  // We surface one option per group rather than listing each column separately.
  // H3 datasets never carry breakdown grouping.
  const breakdownGroupOptions = useMemo(() => {
    if (isH3Dataset) return [];

    const source = analysisId
      ? (rawDatasets?.[datasetId]?.columns ?? [])
      : (datasets[datasetId]?.data.schema ?? []);

    return [
      ...new Set(
        source
          .filter((s) => s.breakdown_group_id)
          .map((s) => s.breakdown_group_id!)
      )
    ].map((id) =>
      toOption(id, removeSpecialCharacters(capitalizeEachWord(id)))
    );
  }, [analysisId, datasetId, datasets, isH3Dataset, rawDatasets]);

  // H3 datasets and post-analysis context both use rawDatasets columns for options.
  const dimensionOptions = useMemo(() => {
    const columns = rawDatasets?.[datasetId]?.columns ?? [];
    const dimensionItems =
      analysisId || isH3Dataset
        ? columns
            .filter((s) => s.is_dimension)
            .map((s) => toOption(s.code || "", s.name))
        : (datasets[datasetId]?.data.schema ?? [])
            .filter((s) => s.is_dimension)
            .map((s) => toOption(s.attribute_column || "", s.attribute_name));

    // BIG_NUMBER has no dimension axis, so a breakdown group is never a valid choice.
    if (widgetType === WidgetType.BIG_NUMBER) return dimensionItems;

    return [...breakdownGroupOptions, ...dimensionItems];
  }, [
    analysisId,
    breakdownGroupOptions,
    datasets,
    datasetId,
    isH3Dataset,
    rawDatasets,
    widgetType
  ]);

  // LINE widgets do not support COUNT_DISTINCT aggregation.
  // when measure have no value, only show COUNT and COUNT_DISTINCT
  const aggregationOptions = useMemo(() => {
    if (widgetType === WidgetType.LINE) {
      return ALL_AGGREGATION_OPTIONS.filter(
        (option) => option.value !== AggregationType.COUNT_DISTINCT
      );
    } else if (!measure && widgetType !== WidgetType.BIG_NUMBER) {
      return ALL_AGGREGATION_OPTIONS.filter((option) =>
        option.value.includes("COUNT")
      );
    } else {
      return ALL_AGGREGATION_OPTIONS;
    }
  }, [widgetType, measure]);

  const measureOptions = useMemo(() => {
    const columns = rawDatasets?.[datasetId]?.columns ?? [];

    // H3 datasets always use rawDataset columns directly; no breakdown grouping applies.
    if (isH3Dataset) {
      return columns
        .filter((s) => s.is_measure)
        .map((s) => toOption(s.code || "", s.name));
    }

    // A breakdown group as dimension already spans every column in that group,
    // so the group itself is the only measure that can be paired with it.
    // BIG_NUMBER has no dimension, so its `dimension` value is never meaningful here.
    const dimensionBreakdownGroup = breakdownGroupOptions.find(
      (option) => option.value === dimension
    );
    if (widgetType !== WidgetType.BIG_NUMBER && dimensionBreakdownGroup)
      return [dimensionBreakdownGroup];

    let measureItems: { value: string; label: string }[];

    if (analysisId) {
      // Post-analysis: source from rawDatasets columns, code → value, name → label
      measureItems = columns
        .filter((s) => s.is_measure)
        .map((s) => toOption(s.code || "", s.name));
    } else {
      const schema = datasets[datasetId]?.data.schema;
      if (!schema) return [];

      measureItems = schema
        .filter((s) => s.is_measure)
        .map((s) => toOption(s.attribute_column || "", s.attribute_name));
    }

    if (widgetType === WidgetType.BIG_NUMBER || widgetType === WidgetType.DONUT)
      return measureItems;

    return [...breakdownGroupOptions, ...measureItems];
  }, [
    analysisId,
    breakdownGroupOptions,
    datasetId,
    datasets,
    dimension,
    isH3Dataset,
    rawDatasets,
    widgetType
  ]);

  const isTimeSeriesDataset = useMemo(() => {
    // Only LINE widgets order by the time dimension.
    if (widgetType !== WidgetType.LINE) return false;
    if (analysisId) return Boolean(rawDatasets?.[datasetId]?.timeseries);
    // Pre-analysis, `group_by` is the sentinel — "none" means cleared.
    const groupBy = datasets[datasetId]?.group_by;
    return Boolean(groupBy) && groupBy !== "none";
  }, [analysisId, datasetId, datasets, rawDatasets, widgetType]);

  return {
    datasetOptions,
    dimensionOptions,
    measureOptions,
    breakdownGroupOptions,
    aggregationOptions,
    orderByOptions,
    dimensionValueShownOptions,
    isTimeSeriesDataset
  };
};

export default useWidgetConfiguration;
